import { inject, Injectable } from '@angular/core';
import {
  CanMatch,
  Params,
  Route,
  Router,
  UrlSegment,
} from '@angular/router';
import { combineLatest, Observable, skipWhile } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

import { AuthorizationFacade } from '../facades/authorization.facade';
import { CustomerFacade } from '../facades/customer.facade';
import { I18nService } from '../services';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanMatch {
  private readonly router: Router = inject(Router);
  private readonly authorizationFacade: AuthorizationFacade = inject(AuthorizationFacade);
  private readonly customerFacade: CustomerFacade = inject(CustomerFacade);
  protected readonly i18nService: I18nService = inject(I18nService);

  /**
   * Checks if the user is authenticated and not a guest.
   * In case user is not authenticated or is a guest, it triggers the authorization process.
   *
   * @param {Route} _route
   * @param {UrlSegment[]} _segments
   * @returns {Observable<boolean>}
   */
  canMatch(
    _route: Route,
    _segments: UrlSegment[],
  ): Observable<boolean> {
    return combineLatest([
      this.authorizationFacade.isLoginInProcess$,
      this.authorizationFacade.isAuthenticated$,
      this.customerFacade.selectIsGuest(),
      this.customerFacade.selectIsCustomerLoaded()
    ]).pipe(
      skipWhile(([isLoginInProcess]: [boolean, boolean, boolean, boolean]) => isLoginInProcess),
      filter(([, isAuthenticated, , isCustomerLoaded]: [boolean, boolean, boolean, boolean]) => !isAuthenticated || (isAuthenticated && isCustomerLoaded)),
      take(1),
      map(([, isAuthenticated, isGuest]: [boolean, boolean, boolean, boolean]) => {
        if (isAuthenticated && !isGuest) {
          return true;
        }

        // If user is not authenticated start authorization process.
        if (!isAuthenticated) {
          const urlParams: URLSearchParams = new URLSearchParams(window.location.search);
          // Get params from url (like fl-number, sysivk etc.) and pass it to authorizationFacade as Params object.
          const params: Params = Object.fromEntries(urlParams.entries());

          // The reason of redirecting to the Homepage is to get rid of short redirect to page not found.
          this.router.navigate([this.i18nService.getCurrentLocale()]).then();
          this.authorizationFacade.authorization(params);
        }
        return false;
      })
    );
  }
}
